import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { AuthContext, API } from '../App';
import { User, LogOut, Crown, CreditCard, CheckCircle2, Loader2 } from 'lucide-react';
import Logo from '../components/Logo';
import axios from 'axios';
import { toast } from 'sonner';

export default function AccountPage() {
  const { user, logout, fetchUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Check for payment success on page load
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.info('Payment status check complete. Please refresh if your plan is not updated.');
      return;
    }

    try {
      const response = await axios.get(`${API}/billing/checkout-status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setCheckingPayment(false);
        setPaymentSuccess(true);
        toast.success('Payment successful! Your plan has been upgraded.');
        // Refresh user data
        await fetchUser();
        // Clear URL params
        setSearchParams({});
        return;
      } else if (data.status === 'expired') {
        setCheckingPayment(false);
        toast.error('Payment session expired. Please try again.');
        setSearchParams({});
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setCheckingPayment(false);
      toast.error('Error verifying payment. Please refresh the page.');
    }
  }, [fetchUser, setSearchParams]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentParam = searchParams.get('payment');

    if (sessionId && paymentParam === 'success') {
      setCheckingPayment(true);
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, pollPaymentStatus]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleUpgrade = async (plan) => {
    setLoading(true);
    try {
      // Get origin URL from frontend
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/billing/create-checkout`, { 
        plan: plan,
        origin_url: originUrl
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      if (error.response?.status === 503) {
        toast.error('Payment system configuration needed. Please contact support.', { duration: 5000 });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      }
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will be reverted to the free plan.')) {
      return;
    }
    setCancelling(true);
    try {
      await axios.post(`${API}/billing/cancel-subscription`);
      toast.success('Subscription cancelled. You are now on the free plan.');
      await fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const plan = user?.subscription_plan || 'free';
  const isActive = user?.subscription_status === 'active';

  const getPlanName = () => {
    if (plan === 'pro') return 'Pro Plan';
    if (plan === 'standard') return 'Standard Plan';
    return 'Free Plan';
  };

  const getPlanLimits = () => {
    if (plan === 'pro') {
      return {
        cvChecks: '1 per day',
        tests: '1 per day',
        cvUpdates: 'Unlimited'
      };
    }
    if (plan === 'standard') {
      return {
        cvChecks: '10 per month',
        tests: '10 per month',
        cvUpdates: '1 per month'
      };
    }
    return {
      cvChecks: '2 total',
      tests: '1 total',
      cvUpdates: '1 only'
    };
  };

  const limits = getPlanLimits();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Logo linkTo="/dashboard" />
          <nav className="flex items-center space-x-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm">Dashboard</Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Payment Processing Banner */}
        {checkingPayment && (
          <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
            <div className="flex items-center">
              <Loader2 className="w-5 h-5 mr-3 animate-spin text-blue-600" />
              <div>
                <p className="font-medium text-blue-800">Processing your payment...</p>
                <p className="text-sm text-blue-600">Please wait while we verify your payment.</p>
              </div>
            </div>
          </Card>
        )}

        {/* Payment Success Banner */}
        {paymentSuccess && (
          <Card className="p-4 mb-6 bg-green-50 border-green-200">
            <div className="flex items-center">
              <CheckCircle2 className="w-5 h-5 mr-3 text-green-600" />
              <div>
                <p className="font-medium text-green-800">Payment Successful!</p>
                <p className="text-sm text-green-600">Your plan has been upgraded successfully.</p>
              </div>
            </div>
          </Card>
        )}

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Account & Billing</h1>
          <p className="text-gray-600">Manage your subscription and account settings</p>
        </div>

        {/* Account Info */}
        <Card className="p-6 mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <User className="w-5 h-5 mr-2" />
            Account Information
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Name:</span>
              <span className="font-medium">{user?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Email:</span>
              <span className="font-medium">{user?.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Role:</span>
              <span className="font-medium capitalize">{user?.role}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Member Since:</span>
              <span className="font-medium">
                {new Date(user?.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </Card>

        {/* Subscription Status */}
        <Card className="p-6 mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <CreditCard className="w-5 h-5 mr-2" />
            Subscription Status
          </h2>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Current Plan</p>
                <p className="text-2xl font-bold flex items-center">
                  {getPlanName()}
                  {isActive && plan !== 'free' && <Crown className="w-6 h-6 ml-2 text-yellow-500" />}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 mb-1">Status</p>
                <p className={`text-lg font-bold capitalize ${isActive ? 'text-green-600' : 'text-gray-600'}`}>
                  {user?.subscription_status || 'trial'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">CV Checks</p>
                <p className="text-lg font-bold">{limits.cvChecks}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">English Tests</p>
                <p className="text-lg font-bold">{limits.tests}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">CV Updates</p>
                <p className="text-lg font-bold">{limits.cvUpdates}</p>
              </div>
            </div>
          </div>

          {plan === 'free' ? (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm font-medium text-yellow-800 mb-2">
                  You&apos;re on the Free Plan
                </p>
                <p className="text-sm text-yellow-700">
                  Upgrade to Pro (AED 49/month) for unlimited CV checks and more features!
                </p>
              </div>
              <Button
                onClick={() => handleUpgrade('pro')}
                disabled={loading}
                className="w-full btn-uae-red"
                data-testid="upgrade-pro-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Upgrade to Pro - AED 49/mo'
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-medium text-green-800 mb-2">
                  {getPlanName()} Member
                </p>
                <p className="text-sm text-green-700">
                  {plan === 'pro' 
                    ? 'You have unlimited CV checks and full access to all features!'
                    : 'You have 10 CV checks and 10 English tests per month!'}
                </p>
              </div>
              
              {plan === 'standard' && (
                <Button
                  onClick={() => handleUpgrade('pro')}
                  disabled={loading}
                  className="w-full btn-uae-green"
                >
                  {loading ? 'Processing...' : 'Upgrade to Pro - AED 49/mo'}
                </Button>
              )}
              
              <Button
                onClick={handleCancelSubscription}
                disabled={cancelling}
                variant="outline"
                className="w-full"
                data-testid="cancel-subscription-btn"
              >
                {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
              </Button>
            </div>
          )}
        </Card>

        {/* Features Comparison */}
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4">Plan Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2">Feature</th>
                  <th className="text-center py-3 px-2">Free</th>
                  <th className="text-center py-3 px-2">Pro</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-3 px-2">ATS CV Checks</td>
                  <td className="text-center py-3 px-2">1 check</td>
                  <td className="text-center py-3 px-2">Unlimited</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-2">CV Fixes</td>
                  <td className="text-center py-3 px-2">—</td>
                  <td className="text-center py-3 px-2">10/month</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-2">Cover Letters</td>
                  <td className="text-center py-3 px-2">—</td>
                  <td className="text-center py-3 px-2">20/month</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-2">Interview Booster</td>
                  <td className="text-center py-3 px-2">Limited</td>
                  <td className="text-center py-3 px-2">Full access</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-2">Apply Templates</td>
                  <td className="text-center py-3 px-2">—</td>
                  <td className="text-center py-3 px-2">✓</td>
                </tr>
                <tr>
                  <td className="py-3 px-2 font-medium">Price</td>
                  <td className="text-center py-3 px-2">Free</td>
                  <td className="text-center py-3 px-2">AED 49/mo</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
